/**
 * ExtractedFieldReviewPanel — check the machine's work, inline.
 *
 * Uploading a document silently populated SED fields, and the ledger said only
 * "extraction hasn't confirmed the required data yet" — never showing WHAT it
 * had extracted. The user could not check the values or correct them without
 * leaving for the Coverage Matrix chip-strip two surfaces away.
 *
 * The three decisions are deliberately asymmetric, and the labels say so:
 *   - Correct    records that a human checked it against the document. The
 *                document stays the source; a check does not upgrade evidence.
 *   - Fix value  replaces it with the human's — a genuine upgrade to authored.
 *   - Wrong      REMOVES it. The load-bearing one: an unreviewed bad extraction
 *                otherwise sits in the SED looking exactly like a good one.
 *
 * Presentational — every write goes through the caller.
 */
import React from 'react';
import { Check, Pencil, X as XIcon, RotateCcw } from 'lucide-react';
import { Button } from '../../ui/button';
import type { StructuredEvidenceValue } from '../../../types/siteEvidence';
import {
  reviewProgress,
  type ExtractedFieldRow,
  type FieldReviewDecision,
} from '../../../lib/coverage/extractedFieldReview';

export interface ExtractedFieldReviewPanelProps {
  rows: readonly ExtractedFieldRow[];
  /** Documents the values came from — shown so the user knows what to check against. */
  documentNames?: readonly string[];
  busyFieldKey?: string | null;
  onDecide: (
    row: ExtractedFieldRow,
    decision: FieldReviewDecision,
    correctedValue?: StructuredEvidenceValue,
  ) => void;
  onUndo: (row: ExtractedFieldRow) => void;
}

/** Render a structured value without lying about empties. */
function displayValue(v: StructuredEvidenceValue | undefined): string {
  if (v === undefined) return '—';
  if (Array.isArray(v)) return v.length === 0 ? '—' : v.join(', ');
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  const s = String(v);
  return s.trim() === '' ? '—' : s;
}

/** `parcel.acres` → `Acres`. The dotted path is an internal identifier. */
function humaniseKey(fieldKey: string): string {
  const spaced = fieldKey
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

const DECISION_LABEL: Record<FieldReviewDecision, string> = {
  accepted: 'Confirmed',
  corrected: 'Corrected',
  rejected: 'Marked wrong — value removed',
};

export const ExtractedFieldReviewPanel: React.FC<ExtractedFieldReviewPanelProps> = ({
  rows,
  documentNames,
  busyFieldKey,
  onDecide,
  onUndo,
}) => {
  const [editingKey, setEditingKey] = React.useState<string | null>(null);
  const [draft, setDraft] = React.useState('');

  // Nothing extracted means there is nothing to check — an empty review panel
  // would imply work that does not exist.
  if (rows.length === 0) return null;

  const progress = reviewProgress(rows);

  const commitEdit = (row: ExtractedFieldRow) => {
    const raw = draft.trim();
    if (raw === '') return;
    // Preserve the original value's type so a numeric field stays numeric —
    // a stringified number silently breaks every downstream comparison.
    const next: StructuredEvidenceValue = typeof row.value === 'number' && !Number.isNaN(Number(raw))
      ? Number(raw)
      : raw;
    onDecide(row, 'corrected', next);
    setEditingKey(null);
    setDraft('');
  };

  return (
    <div
      className="mt-3 rounded-md border border-border-subtle bg-surface-2 p-3"
      data-testid="extracted-field-review-panel"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="type-meta">What the document filled in</span>
        <span className="type-body text-text-muted" data-testid="extracted-field-review-progress">
          {progress.unreviewed > 0
            ? `${progress.unreviewed} of ${progress.total} still to check`
            : `All ${progress.total} checked`}
        </span>
      </div>
      {documentNames && documentNames.length > 0 && (
        <p className="mt-1 type-body text-text-muted break-words">
          Check these against {documentNames.join(', ')}.
        </p>
      )}

      <ul className="mt-2 space-y-1.5" data-testid="extracted-field-review-rows">
        {rows.map(row => {
          const busy = busyFieldKey === row.fieldKey;
          const editing = editingKey === row.fieldKey;
          return (
            <li
              key={row.fieldPath}
              className="rounded-md border border-border bg-background p-2"
              data-testid={`extracted-field-${row.fieldKey}`}
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <span className="type-body font-semibold text-text-primary">
                    {humaniseKey(row.fieldKey)}
                  </span>
                  <span className="ml-2 type-body text-text-secondary break-words">
                    {displayValue(row.value)}
                  </span>
                </div>
                {row.review ? (
                  <div className="flex items-center gap-2">
                    <span
                      className={`type-body ${row.review.decision === 'rejected' ? 'text-warning' : 'text-text-muted'}`}
                      data-testid={`extracted-field-decision-${row.fieldKey}`}
                    >
                      {DECISION_LABEL[row.review.decision]}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7"
                      disabled={busy}
                      onClick={() => onUndo(row)}
                      data-testid={`extracted-field-undo-${row.fieldKey}`}
                    >
                      <RotateCcw className="mr-1 h-3 w-3" aria-hidden="true" />
                      Undo
                    </Button>
                  </div>
                ) : (
                  <div className="flex shrink-0 items-center gap-1.5">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7"
                      disabled={busy}
                      onClick={() => onDecide(row, 'accepted')}
                      data-testid={`extracted-field-accept-${row.fieldKey}`}
                    >
                      <Check className="mr-1 h-3 w-3" aria-hidden="true" />
                      Correct
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7"
                      disabled={busy}
                      onClick={() => {
                        setEditingKey(row.fieldKey);
                        setDraft(row.value === undefined ? '' : String(row.value));
                      }}
                      data-testid={`extracted-field-edit-${row.fieldKey}`}
                    >
                      <Pencil className="mr-1 h-3 w-3" aria-hidden="true" />
                      Fix value
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7"
                      disabled={busy}
                      onClick={() => onDecide(row, 'rejected')}
                      data-testid={`extracted-field-reject-${row.fieldKey}`}
                    >
                      <XIcon className="mr-1 h-3 w-3" aria-hidden="true" />
                      Wrong
                    </Button>
                  </div>
                )}
              </div>

              {editing && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <input
                    autoFocus
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') commitEdit(row);
                      if (e.key === 'Escape') { setEditingKey(null); setDraft(''); }
                    }}
                    className="min-w-0 flex-1 rounded-md border border-border bg-background px-2 py-1 type-body text-text-primary"
                    data-testid={`extracted-field-input-${row.fieldKey}`}
                  />
                  <Button
                    size="sm"
                    className="h-7"
                    disabled={draft.trim() === '' || busy}
                    onClick={() => commitEdit(row)}
                    data-testid={`extracted-field-save-${row.fieldKey}`}
                  >
                    Save
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7"
                    onClick={() => { setEditingKey(null); setDraft(''); }}
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};
